import { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout.jsx";
import { locations as locsApi, locationMaps, getAccount } from "../lib/supabase.js";
import MapConfigPanel from "../components/MapConfigPanel.jsx";
import LocationEditor from "../components/LocationEditor.jsx";
import CSVImporter from "../components/CSVImporter.jsx";
import MapWidgetPreview from "../components/MapWidgetPreview.jsx";
import { useUserRole, hasPermission } from "../App.jsx";

// ─── Font loader ─────────────────────────────────────────────────────────────

function useFonts() {
  useEffect(() => {
    [
      { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
      { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap" },
    ].forEach((f) => {
      if (!document.getElementById(f.id)) {
        const link = document.createElement("link");
        link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
        document.head.appendChild(link);
      }
    });
  }, []);
}

// ─── Theme ───────────────────────────────────────────────────────────────────

const L = {
  bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a",
  dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
  surface: "#ffffff", surfaceHover: "#f6f5f2", inputBg: "#f6f5f2",
};
const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";

const inputStyle = {
  width: "100%", padding: "9px 14px", background: L.inputBg,
  border: `1px solid ${L.border}`, borderRadius: 8, color: L.text,
  fontSize: 13, outline: "none", fontFamily: sans, boxSizing: "border-box",
};
const btnGhost = {
  padding: "8px 16px", background: "none", border: `1px solid ${L.border}`,
  borderRadius: 8, color: L.muted, fontSize: 12, fontWeight: 500,
  cursor: "pointer", fontFamily: sans, whiteSpace: "nowrap",
};

// ─── Component ───────────────────────────────────────────────────────────────

export default function Locations() {
  useFonts();
  const account = getAccount();
  const color = account?.widget_color || "#6366f1";
  const { role } = useUserRole();
  const canEdit = hasPermission(role, "editContent");
  const [maps, setMaps] = useState([]); const [activeMapId, setActiveMapId] = useState(null);
  const [locs, setLocs] = useState([]); const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(""); const [selected, setSelected] = useState(new Set());
  const [showMapConfig, setShowMapConfig] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [liveConfig, setLiveConfig] = useState(null);

  const activeMap = maps.find((m) => m.id === activeMapId) || null;
  const platformUrl = import.meta.env.VITE_SUPABASE_URL;

  const loadMaps = useCallback(async () => { const { data } = await locationMaps.list(); setMaps(data || []); if (data?.length && !activeMapId) setActiveMapId(data[0].id); setLoading(false); }, [activeMapId]);
  useEffect(() => { loadMaps(); }, []);
  const loadLocs = useCallback(async () => { if (!activeMapId) { setLocs([]); return; } const { data } = await locsApi.list({ map_id: activeMapId }); setLocs(data || []); setSelected(new Set()); }, [activeMapId]);
  useEffect(() => { loadLocs(); }, [activeMapId, loadLocs]);

  const refresh = () => { loadLocs(); loadMaps(); setPreviewRefreshKey((k) => k + 1); setLiveConfig(null); };
  const filtered = locs.filter((l) => l.name?.toLowerCase().includes(search.toLowerCase()) || l.city?.toLowerCase().includes(search.toLowerCase()) || l.state_province?.toLowerCase().includes(search.toLowerCase()) || l.formatted_address?.toLowerCase().includes(search.toLowerCase()) || l.phone?.includes(search));
  const handleDeleteLocation = async (id) => { if (!confirm("Delete this location?")) return; await locsApi.delete(id); refresh(); };
  const handleBulkDelete = async () => { if (!confirm(`Delete ${selected.size} selected?`)) return; for (const id of selected) await locsApi.delete(id); refresh(); };
  const handleDeleteMap = async () => { if (!confirm(`Delete "${activeMap?.name}" and all locations?`)) return; await locsApi.deleteByMap(activeMapId); await locationMaps.delete(activeMapId); setActiveMapId(null); loadMaps(); };
  const toggleSelect = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => { selected.size === filtered.length ? setSelected(new Set()) : setSelected(new Set(filtered.map((l) => l.id))); };
  const getFieldDisplay = (loc, f) => { const v = loc.attributes?.[f.key]; if (v == null || v === "") return null; if (f.type === "boolean") return v ? "✓" : "—"; if (Array.isArray(v)) return v.join(", "); return String(v); };

  const customFields = activeMap?.custom_fields || [];
  const stats = [
    { label: "Total", value: locs.length },
    { label: "Active", value: locs.filter((l) => l.is_active).length },
    { label: "Types", value: [...new Set(locs.map((l) => l.location_type).filter(Boolean))].length },
  ];

  const embedCode = activeMap ? `<!-- Clarifico Map Widget: ${activeMap.name} -->
<div id="clarifico-map-${activeMap.slug}" class="clarifico-map-widget"></div>
<script src="https://cdn.clarifico.io/widget-map.js"
  data-account="${account?.slug}"
  data-map="${activeMap.slug}"
  data-api="${platformUrl}/functions/v1"
  data-container="clarifico-map-${activeMap.slug}"
  async></script>` : "";
  const copyEmbed = () => { navigator.clipboard.writeText(embedCode); setEmbedCopied(true); setTimeout(() => setEmbedCopied(false), 2000); };

  return (
    <Layout>
      <style>{`
        @keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      <div style={{
        minHeight: "calc(100vh - 56px)", background: L.bg,
        margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text,
      }}>
        {/* ── Header ── */}
        <div style={{ marginBottom: 28, animation: "ov-up 0.5s ease both" }}>
          <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>
            Locations
          </h1>
          <p style={{ margin: 0, fontSize: 13, color: L.muted }}>Manage maps, dealers, and service network</p>
        </div>

        {/* ── Map tabs ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap", animation: "ov-up 0.5s ease 0.05s both" }}>
          {maps.map((m) => {
            const active = activeMapId === m.id;
            return (
              <button
                key={m.id}
                onClick={() => { setActiveMapId(m.id); setSearch(""); setSelected(new Set()); setShowEmbed(false); setLiveConfig(null); }}
                style={{
                  padding: "9px 18px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                  cursor: "pointer", fontFamily: sans, transition: "all 0.15s",
                  background: active ? L.text : "none",
                  color: active ? L.bg : L.muted,
                  border: `1px solid ${active ? L.text : L.border}`,
                }}
              >
                {m.name} <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.6 }}>{m.location_count || 0}</span>
              </button>
            );
          })}
          {canEdit && (
            <button
              onClick={() => setShowMapConfig("new")}
              style={{
                padding: "9px 18px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                cursor: "pointer", fontFamily: sans,
                background: "none", color: L.muted, border: `1px dashed ${L.border}`,
              }}
            >
              + Create map
            </button>
          )}
        </div>

        {/* Empty state */}
        {maps.length === 0 && !loading && (
          <div style={{
            background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
            padding: "64px 20px", textAlign: "center",
          }}>
            <div style={{ fontSize: 14, color: L.muted, marginBottom: 20 }}>No maps yet.</div>
            {canEdit && (
              <button onClick={() => setShowMapConfig("new")} style={{
                padding: "10px 24px", background: L.text, border: "none", borderRadius: 8,
                color: L.bg, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans,
              }}>
                Create map
              </button>
            )}
          </div>
        )}

        {activeMap && (<>
          {/* ── Toolbar ── */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", animation: "ov-up 0.5s ease 0.08s both" }}>
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${activeMap.name}...`}
              style={{ ...inputStyle, flex: 1, minWidth: 200 }}
            />
            {canEdit && (
              <button onClick={() => setEditingLocation("new")} style={{
                padding: "9px 20px", background: L.text, border: "none", borderRadius: 8,
                color: L.bg, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: sans,
              }}>
                + Add
              </button>
            )}
            {canEdit && <button onClick={() => setShowCSVImport(true)} style={btnGhost}>Import CSV</button>}
            {canEdit && <button onClick={() => setShowMapConfig(activeMap)} style={btnGhost}>Settings</button>}
            <button
              onClick={() => setShowEmbed(!showEmbed)}
              style={{
                ...btnGhost,
                background: showEmbed ? L.inputBg : "none",
                color: showEmbed ? L.text : L.muted,
                borderColor: showEmbed ? L.text : L.border,
              }}
            >
              {showEmbed ? "Close embed" : "Embed"}
            </button>
            {canEdit && selected.size > 0 && (
              <button onClick={handleBulkDelete} style={{
                ...btnGhost, color: "#dc2626", borderColor: "#fecaca",
              }}>
                Delete {selected.size}
              </button>
            )}
          </div>

          {/* ── Stats ── */}
          <div style={{
            display: "flex", gap: 0, marginBottom: 16,
            borderTop: `1px solid ${L.border}`, borderBottom: `1px solid ${L.border}`,
            animation: "ov-up 0.5s ease 0.1s both",
          }}>
            {stats.map((s, i) => (
              <div key={s.label} style={{
                padding: "12px 18px",
                borderRight: i < stats.length - 1 ? `1px solid ${L.border}` : "none",
              }}>
                <span style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: L.muted }}>{s.label} </span>
                <span style={{ fontSize: 18, fontWeight: 300, letterSpacing: "-0.02em" }}>{s.value}</span>
              </div>
            ))}
          </div>

          {/* ── Embed panel ── */}
          {showEmbed && (
            <div style={{
              background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
              padding: 22, marginBottom: 18, animation: "ov-up 0.3s ease both",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                <div style={{ fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em" }}>Embed — {activeMap.name}</div>
                <button onClick={copyEmbed} style={{
                  ...btnGhost, padding: "5px 12px", fontSize: 11,
                  color: embedCopied ? "#15803d" : L.text,
                  borderColor: embedCopied ? "#bbf7d0" : L.border,
                  background: embedCopied ? "#f0fdf4" : "none",
                }}>
                  {embedCopied ? "Copied" : "Copy code"}
                </button>
              </div>
              <pre style={{
                margin: 0, padding: 16, background: L.text, borderRadius: 8,
                color: "#a5b4fc", fontSize: 11, fontFamily: "'DM Mono', monospace",
                overflowX: "auto", lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-all",
              }}>{embedCode}</pre>
              <div style={{
                marginTop: 10, fontSize: 12, color: L.muted, lineHeight: 1.6,
                padding: "10px 12px", background: L.inputBg, borderRadius: 8, border: `1px solid ${L.borderLight}`,
              }}>
                The embed code stays the same — the widget loads settings from the server at runtime. After saving Map Settings, the embedded widget automatically reflects changes.
              </div>
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>
                  Live preview {liveConfig && <span style={{ fontWeight: 400, textTransform: "none", color: "#b45309", fontSize: 10, marginLeft: 6 }}>unsaved changes</span>}
                </div>
                <MapWidgetPreview accountSlug={account?.slug} mapSlug={activeMap.slug} refreshKey={previewRefreshKey} liveConfig={liveConfig} />
              </div>
            </div>
          )}

          {/* ── Location table ── */}
          <div style={{
            background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
            overflow: "hidden", animation: "ov-up 0.5s ease 0.12s both",
          }}>
            {/* Header */}
            <div style={{
              display: "flex", padding: "12px 20px", borderBottom: `1px solid ${L.border}`,
              fontSize: 10, color: L.muted, fontWeight: 600, textTransform: "uppercase",
              letterSpacing: "0.1em", gap: 12, alignItems: "center",
            }}>
              <div style={{ width: 24 }}>{canEdit && <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleSelectAll} style={{ cursor: "pointer" }} />}</div>
              <div style={{ flex: 2 }}>Name</div>
              <div style={{ flex: 2 }}>Address</div>
              <div style={{ width: 110 }}>Phone</div>
              {customFields.slice(0, 3).map((cf) => <div key={cf.key} style={{ width: 90 }}>{cf.label}</div>)}
              <div style={{ width: 80 }}>Type</div>
              <div style={{ width: 50, textAlign: "center" }}></div>
            </div>

            {/* Rows */}
            {loading ? (
              <div style={{ padding: "40px 20px", color: L.muted, fontSize: 13, textAlign: "center" }}>Loading...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: "48px 20px", color: L.muted, fontSize: 13, textAlign: "center", lineHeight: 1.7 }}>
                {locs.length === 0 ? "No locations yet." : "No matches."}
              </div>
            ) : filtered.map((l) => (
              <div
                key={l.id}
                style={{
                  display: "flex", padding: "14px 20px", borderBottom: `1px solid ${L.borderLight}`,
                  alignItems: "center", gap: 12, opacity: l.is_active ? 1 : 0.45,
                  cursor: "pointer", transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = L.surfaceHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = L.surface; }}
              >
                <div style={{ width: 24 }}>
                  {canEdit && <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSelect(l.id)} onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }} />}
                </div>
                <div style={{ flex: 2 }} onClick={() => canEdit && setEditingLocation(l)}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{l.name}</div>
                </div>
                <div style={{ flex: 2, fontSize: 12, color: L.muted }}>
                  {l.formatted_address || [l.address_line1, l.city, l.state_province].filter(Boolean).join(", ")}
                </div>
                <div style={{ width: 110, fontSize: 12, color: L.muted }}>{l.phone}</div>
                {customFields.slice(0, 3).map((cf) => {
                  const v = getFieldDisplay(l, cf);
                  return <div key={cf.key} style={{ width: 90, fontSize: 12, color: v ? L.textSoft : L.dim }}>{v || "—"}</div>;
                })}
                <div style={{ width: 80 }}>
                  {l.location_type && (
                    <span style={{
                      padding: "2px 8px", borderRadius: 20, fontSize: 10, fontWeight: 600,
                      textTransform: "uppercase", background: L.inputBg, color: L.muted,
                      border: `1px solid ${L.borderLight}`, letterSpacing: "0.04em",
                    }}>
                      {l.location_type}
                    </span>
                  )}
                </div>
                <div style={{ width: 50, display: "flex", justifyContent: "center", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 7, height: 7, borderRadius: "50%", background: l.is_active ? "#16a34a" : L.dim }} />
                  {canEdit && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteLocation(l.id); }}
                      style={{ background: "none", border: "none", color: L.dim, cursor: "pointer", fontSize: 13, transition: "color 0.15s" }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#dc2626"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = L.dim; }}
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ── Delete map ── */}
          {canEdit && (
            <div style={{
              marginTop: 24, padding: "16px 20px", background: L.surface,
              border: `1px solid ${L.border}`, borderRadius: 10,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>Delete map</div>
                <div style={{ fontSize: 12, color: L.muted, marginTop: 2 }}>Remove "{activeMap.name}" and all {locs.length} locations.</div>
              </div>
              <button onClick={handleDeleteMap} style={{
                ...btnGhost, color: "#dc2626", borderColor: "#fecaca",
              }}>
                Delete map
              </button>
            </div>
          )}
        </>)}

        {/* ── Modals (these sub-components still use dark styling internally) ── */}
        {canEdit && showMapConfig && (
          <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(0,0,0,0.25)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
            <div style={{ width: 700, maxHeight: "90vh" }}>
              <MapConfigPanel map={showMapConfig === "new" ? null : showMapConfig} color={color} onSave={(s) => { loadMaps(); if (s?.id) setActiveMapId(s.id); setShowMapConfig(false); refresh(); }} onClose={() => { setShowMapConfig(false); setLiveConfig(null); }} onLiveConfig={setLiveConfig} />
            </div>
          </div>
        )}
        {canEdit && editingLocation && <LocationEditor location={editingLocation === "new" ? null : editingLocation} map={activeMap} color={color} typeOptions={[...new Set(locs.map((l) => l.location_type).filter(Boolean))].sort()} onSave={refresh} onClose={() => setEditingLocation(null)} />}
        {canEdit && showCSVImport && activeMap && <CSVImporter map={activeMap} color={color} onComplete={refresh} onClose={() => setShowCSVImport(false)} />}
      </div>
    </Layout>
  );
}
